import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Target,
    Sparkles,
    Route,
    Globe,
    Megaphone,
    Cpu,
    Clock,
    Video,
    CalendarDays,
    Check,
    ArrowRight,
    Star,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionEyebrow from "@/components/SectionEyebrow";
import Seo from "@/components/Seo";
import ClientLogos from "@/components/ClientLogos";
import { useLang } from "@/context/LanguageContext";
import { CONSULTATION_PRICE } from "@/lib/config";
import FAQ from "@/components/FAQ";
import { consultationFaqs } from "@/data/faqs";
import { serviceJsonLd, faqJsonLd, breadcrumbJsonLd } from "@/lib/seo";

const benefits = [
    { icon: Target, title: "Business Clarity", body: "Know exactly what is working, what is not, and where to focus next." },
    { icon: Sparkles, title: "Brand Positioning", body: "Make your business look stronger, clearer, and more premium." },
    { icon: Route, title: "Growth Roadmap", body: "A practical 3 to 12-month plan built for your stage." },
    { icon: Globe, title: "Digital Visibility", body: "Fix your website, content, and online presence the right way." },
    { icon: Megaphone, title: "Marketing Direction", body: "Stop guessing. Get a clear direction for marketing and sales." },
    { icon: Cpu, title: "AI & Automation Guidance", body: "Use modern tools to move faster without losing your brand voice." },
];

const whoFor = [
    "Founders and small business owners",
    "Personal brands, coaches, and creators",
    "Local businesses ready to look national",
    "Businesses stuck in slow or flat growth",
    "Anyone planning a new brand or relaunch",
];

const youGet = [
    "An honest review of your business and brand",
    "Clear positioning and messaging direction",
    "Website and digital presence fixes",
    "Lead generation and content direction",
    "A simple, prioritised action plan",
    "Direct answers to your specific questions",
];

const steps = [
    "Select a date",
    "Choose your slot",
    "Make secure payment",
    "Share your business details",
    "Get your confirmation",
];

const testimonials = [
    { name: "Manjunath R", role: "Business Owner", text: "Skilled, responsive, and genuinely passionate. The insights I got reshaped how I think about my brand." },
    { name: "Aishwarya", role: "Brand Founder", text: "A game-changer for our brand. The direction was clear and it directly boosted our reach and sales." },
    { name: "Omnath C P", role: "Entrepreneur", text: "Stunning ideas and real support in building my brand. Abhijit's suggestions made a genuine difference." },
];

export default function Consultation() {
    const { t } = useLang();
    return (
        <div data-testid="consultation-page" className="relative">
            <Seo
                title="1-Hour Business Consultation — ₹1,999 · Abhijit JK"
                description="Book a ₹1,999 one-hour one-to-one business consultation with Abhijit JK. Get clarity, brand positioning, digital growth direction and a practical 3 to 12-month action plan. Online across India."
                keywords="business growth roadmap, business consultation, 1-on-1 consultation, brand positioning, marketing strategy, growth consultant Bangalore, business coach India, Abhijit JK"
                image="/images/consultation-hero.jpg"
                path="/consultation"
                jsonLd={[
                    serviceJsonLd({
                        name: "1-Hour Business Consultation",
                        description: "One-to-one consultation delivering clarity, brand positioning, digital growth direction and a practical 3 to 12-month action plan.",
                        price: 1999,
                        url: "/consultation",
                    }),
                    faqJsonLd(consultationFaqs),
                    breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Consultation", path: "/consultation" }]),
                ]}
            />

            {/* HERO / LANDING */}
            <section className="mx-auto grid max-w-7xl items-center gap-12 px-6 pt-16 pb-16 lg:grid-cols-12 lg:px-10 lg:pt-20">
                <div className="lg:col-span-6">
                    <Reveal>
                        <SectionEyebrow>{t("consult_eyebrow")}</SectionEyebrow>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h1 className="mt-6 font-display text-4xl font-semibold leading-[1.05] tracking-tighter sm:text-5xl lg:text-6xl">
                            {t("consult_pre")}{" "}
                            <span className="gold-text">{t("consult_gold")}</span>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-6 max-w-xl text-base leading-relaxed text-white/65 md:text-lg">
                            {t("consult_sub")}
                        </p>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <div className="mt-8 flex flex-wrap items-center gap-5 text-sm text-white/65">
                            <span className="inline-flex items-center gap-2"><Clock size={15} className="text-gold-300" /> 60 minutes · Mon–Fri</span>
                            <span className="inline-flex items-center gap-2"><Video size={15} className="text-gold-300" /> Online · Google Meet</span>
                            <span className="gold-text font-display text-xl font-semibold">{CONSULTATION_PRICE}</span>
                        </div>
                    </Reveal>
                    <Reveal delay={0.25}>
                        <Link
                            to="/booking"
                            data-testid="consultation-hero-cta"
                            className="gold-button mt-9 inline-flex items-center gap-2"
                        >
                            {t("btn_book_paid")} <ArrowRight size={14} />
                        </Link>
                    </Reveal>
                </div>

                <Reveal delay={0.1} className="lg:col-span-6">
                    <div className="relative mx-auto max-w-[560px] overflow-hidden rounded-lg">
                        <img
                            src="/images/consultation-hero.jpg"
                            alt="Abhijit JK — Business Strategy Consultation"
                            data-testid="consultation-hero-image"
                            className="h-full w-full object-cover"
                        />
                    </div>
                </Reveal>
            </section>

            {/* BENEFITS */}
            <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
                <Reveal>
                    <SectionEyebrow>What You Walk Away With</SectionEyebrow>
                </Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-5 max-w-3xl font-display text-3xl font-medium tracking-tight sm:text-4xl lg:text-5xl">
                        Six outcomes from{" "}
                        <span className="gold-text">one focused session.</span>
                    </h2>
                </Reveal>
                <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {benefits.map((b, i) => {
                        const Icon = b.icon;
                        return (
                            <Reveal key={b.title} delay={i * 0.07}>
                                <div data-testid={`benefit-card-${i}`} className="glass glass-hover h-full rounded-md p-8">
                                    <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-gold-300/10 text-gold-300">
                                        <Icon size={18} />
                                    </div>
                                    <h3 className="mt-6 font-display text-xl font-medium">{b.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-white/60">{b.body}</p>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            {/* WHO + WHAT YOU GET */}
            <section className="mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-2 lg:px-10">
                <Reveal>
                    <div className="glass rounded-lg p-8 md:p-10">
                        <p className="label-eyebrow">Who this is for</p>
                        <ul className="mt-6 space-y-4">
                            {whoFor.map((w) => (
                                <li key={w} className="flex items-start gap-3 text-sm text-white/80 md:text-base">
                                    <Check size={15} className="mt-1 flex-none text-gold-300" /> {w}
                                </li>
                            ))}
                        </ul>
                    </div>
                </Reveal>
                <Reveal delay={0.1}>
                    <div className="glass glow-border rounded-lg p-8 md:p-10">
                        <p className="label-eyebrow">What you will get</p>
                        <ul className="mt-6 space-y-4">
                            {youGet.map((w) => (
                                <li key={w} className="flex items-start gap-3 text-sm text-white/80 md:text-base">
                                    <Check size={15} className="mt-1 flex-none text-gold-300" /> {w}
                                </li>
                            ))}
                        </ul>
                    </div>
                </Reveal>
            </section>

            {/* HOW IT WORKS */}
            <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
                <Reveal>
                    <SectionEyebrow>How It Works</SectionEyebrow>
                </Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-5 font-display text-3xl font-medium sm:text-4xl">
                        A simple 5-step booking
                    </h2>
                </Reveal>
                <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
                    {steps.map((s, i) => (
                        <Reveal key={s} delay={i * 0.07}>
                            <div className="glass glass-hover h-full rounded-md p-6">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full glass glow-border font-display gold-text">
                                    {i + 1}
                                </div>
                                <p className="mt-5 text-sm text-white/85 md:text-base">{s}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* CLIENT LOGOS */}
            <section className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
                <Reveal>
                    <p className="text-center label-eyebrow">Brands We Have Worked With</p>
                </Reveal>
                <div className="mt-10">
                    <ClientLogos />
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
                <Reveal>
                    <SectionEyebrow>Client Words</SectionEyebrow>
                </Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-5 max-w-3xl font-display text-3xl font-medium sm:text-4xl lg:text-5xl">
                        Real feedback from people I&apos;ve{" "}
                        <span className="gold-text">worked with.</span>
                    </h2>
                </Reveal>
                <div className="mt-12 grid gap-6 md:grid-cols-3">
                    {testimonials.map((t, i) => (
                        <Reveal key={t.name} delay={i * 0.08}>
                            <div data-testid={`consult-testimonial-${i}`} className="glass glow-border flex h-full flex-col rounded-lg p-8">
                                <div className="flex gap-1 text-gold-300">
                                    {[...Array(5)].map((_, s) => <Star key={s} size={14} fill="currentColor" />)}
                                </div>
                                <p className="mt-5 flex-1 text-base leading-relaxed text-white/75">&ldquo;{t.text}&rdquo;</p>
                                <div className="mt-6 border-t border-white/10 pt-5">
                                    <p className="font-display text-lg">{t.name}</p>
                                    <p className="text-sm text-white/50">{t.role}</p>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <FAQ items={consultationFaqs} eyebrow="FAQ" title="Consultation —" gold="common questions." />

            {/* INTERNAL LINKS */}
            <section className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
                <p className="label-eyebrow">Keep Exploring</p>
                <div className="mt-5 flex flex-wrap gap-3">
                    {[
                        ["Business Growth Workshops", "/workshops"],
                        ["8:00–9:00 AM & 9:00–10:00 AM slots", "/booking"],
                        ["Case Studies", "/case-studies"],
                        ["Business Growth Blog", "/blog"],
                    ].map(([label, to]) => (
                        <Link key={to} to={to} className="glass glass-hover inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm text-white/80 transition hover:text-gold-300">
                            {label} <ArrowRight size={12} />
                        </Link>
                    ))}
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
                <Reveal>
                    <div className="glass glow-border relative overflow-hidden rounded-lg p-10 text-center md:p-16">
                        <span className="pointer-events-none absolute -top-32 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full opacity-40 blur-3xl" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.4), transparent 60%)" }} />
                        <div className="relative">
                            <CalendarDays className="mx-auto text-gold-300" size={32} />
                            <h2 className="mx-auto mt-6 max-w-3xl font-display text-3xl font-medium leading-tight sm:text-4xl lg:text-5xl">
                                One conversation can change your{" "}
                                <span className="gold-text">next 12 months.</span>
                            </h2>
                            <Link to="/booking" data-testid="consultation-final-cta" className="gold-button mt-10 inline-flex items-center gap-2">
                                {t("btn_book")} <ArrowRight size={14} />
                            </Link>
                        </div>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
